/**
 * Pure geometry helpers for floor-plan resource positioning.
 *
 * Used from both NavigationEdit.vue (ES import) and the Filament
 * resource-position Blade view (via `window.geometryUtils`, set in app.js).
 * Keep this module free of Vue, Alpine and Leaflet so both call sites can
 * share the same implementation.
 */

const DEFAULT_SQUARED_COLOR_THRESHOLD = 100;

/**
 * Flood-fill a similarly-coloured region around `start` on a raw RGBA
 * pixel buffer, then return the principal-axis oriented bounding box of
 * the resulting cluster (via PCA).
 *
 * `pixels` matches the shape of `ImageData`.
 *
 * Returns `null` when `start` lies outside the buffer.
 *
 * @param {{ x: number, y: number }} start
 * @param {{ data: ArrayLike<number>, width: number, height: number }} pixels
 * @param {{ colorThreshold?: number }} [options]
 * @returns {{
 *   center: { x: number, y: number },
 *   halfWidth: number,
 *   halfHeight: number,
 *   angleDegrees: number,
 *   pointCount: number,
 *   aspectRatio: number,
 *   fillRatio: number,
 *   degenerate: boolean,
 * } | null}
 */
export function floodFillOBB(start, pixels, options = {}) {
    const { data, width, height } = pixels;
    const threshold = options.colorThreshold ?? DEFAULT_SQUARED_COLOR_THRESHOLD;

    const sx = start.x | 0;
    const sy = start.y | 0;
    if (sx < 0 || sy < 0 || sx >= width || sy >= height) {
        return null;
    }

    const visited = new Uint8Array(width * height);
    visited[sy * width + sx] = 1;

    const stack = [[sx, sy]];
    const points = [{ x: sx, y: sy }];

    let left = sx;
    let right = sx;
    let top = sy;
    let bottom = sy;

    while (stack.length > 0) {
        const visiting = stack.pop();
        const vx = visiting[0];
        const vy = visiting[1];

        const masterIndex = (vy * width + vx) * 4;
        const mr = data[masterIndex];
        const mg = data[masterIndex + 1];
        const mb = data[masterIndex + 2];

        const neighbours = [
            [vx + 1, vy],
            [vx - 1, vy],
            [vx, vy + 1],
            [vx, vy - 1],
        ];

        for (let i = 0; i < neighbours.length; i++) {
            const nx = neighbours[i][0];
            const ny = neighbours[i][1];
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
                continue;
            }
            const flat = ny * width + nx;
            if (visited[flat]) {
                continue;
            }
            visited[flat] = 1;

            const neighbourIndex = flat * 4;
            const dr = data[neighbourIndex] - mr;
            const dg = data[neighbourIndex + 1] - mg;
            const db = data[neighbourIndex + 2] - mb;
            if (dr * dr + dg * dg + db * db >= threshold) {
                continue;
            }

            if (nx < left) { left = nx; }
            if (nx > right) { right = nx; }
            if (ny < top) { top = ny; }
            if (ny > bottom) { bottom = ny; }

            stack.push([nx, ny]);
            points.push({ x: nx, y: ny });
        }
    }

    // Degenerate cluster (collinear / too few samples) — fall back to AABB.
    if (points.length < 3) {
        return {
            center: { x: (left + right) / 2, y: (top + bottom) / 2 },
            halfWidth: (right - left) / 2,
            halfHeight: (bottom - top) / 2,
            angleDegrees: 0,
            pointCount: points.length,
            aspectRatio: 1,
            fillRatio: 0,
            degenerate: true,
        };
    }

    const n = points.length;
    let meanX = 0;
    let meanY = 0;
    for (let i = 0; i < n; i++) {
        meanX += points[i].x;
        meanY += points[i].y;
    }
    meanX /= n;
    meanY /= n;

    let cxx = 0;
    let cyy = 0;
    let cxy = 0;
    for (let i = 0; i < n; i++) {
        const dx = points[i].x - meanX;
        const dy = points[i].y - meanY;
        cxx += dx * dx;
        cyy += dy * dy;
        cxy += dx * dy;
    }
    cxx /= n;
    cyy /= n;
    cxy /= n;

    let thetaImage = 0.5 * Math.atan2(2 * cxy, cxx - cyy);

    // Pick the axis closer to the image x-axis as "local x" so width stays
    // the more horizontal extent (avoids 90° flips on tall axis-aligned rects).
    let cos = Math.cos(thetaImage);
    let sin = Math.sin(thetaImage);
    if (Math.abs(cos) < Math.abs(sin)) {
        thetaImage -= (thetaImage >= 0 ? 1 : -1) * Math.PI / 2;
        cos = Math.cos(thetaImage);
        sin = Math.sin(thetaImage);
    }

    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (let i = 0; i < n; i++) {
        const dx = points[i].x - meanX;
        const dy = points[i].y - meanY;
        const u = dx * cos + dy * sin;
        const v = -dx * sin + dy * cos;
        if (u < minU) { minU = u; }
        if (u > maxU) { maxU = u; }
        if (v < minV) { minV = v; }
        if (v > maxV) { maxV = v; }
    }

    const centerU = (minU + maxU) / 2;
    const centerV = (minV + maxV) / 2;
    const centerImageX = meanX + centerU * cos - centerV * sin;
    const centerImageY = meanY + centerU * sin + centerV * cos;

    const halfWidth = (maxU - minU) / 2;
    const halfHeight = (maxV - minV) / 2;
    const obbArea = (2 * halfWidth) * (2 * halfHeight);

    return {
        center: { x: centerImageX, y: centerImageY },
        halfWidth,
        halfHeight,
        // Image y points down, latlng y points up — flip the sign for storage.
        angleDegrees: -thetaImage * 180 / Math.PI,
        pointCount: n,
        aspectRatio: Math.max(halfWidth, halfHeight) / Math.max(Math.min(halfWidth, halfHeight), 1),
        fillRatio: obbArea > 0 ? n / obbArea : 0,
        degenerate: false,
    };
}

/**
 * A rectangle (even rotated) fills its OBB; a circle covers only
 * π/4 ≈ 0.785 of its surrounding square. Combined with a near-1 aspect
 * ratio that is a reliable circle classifier.
 *
 * @param {ReturnType<typeof floodFillOBB>} obb
 * @returns {boolean}
 */
export function looksLikeCircle(obb) {
    if (!obb || obb.degenerate) {
        return false;
    }
    return obb.aspectRatio < 1.3 && obb.fillRatio < 0.85;
}

/**
 * Rotate a `{lat, lng}` point around `center` by `angleRadians`.
 * Returns a plain object; wrap in `L.latLng(...)` at the call site if needed.
 *
 * @param {{ lat: number, lng: number }} point
 * @param {{ lat: number, lng: number }} center
 * @param {number} angleRadians
 * @returns {{ lat: number, lng: number }}
 */
export function rotatePoint(point, center, angleRadians) {
    const cos = Math.cos(angleRadians);
    const sin = Math.sin(angleRadians);
    const x = center.lng + (point.lng - center.lng) * cos - (point.lat - center.lat) * sin;
    const y = center.lat + (point.lng - center.lng) * sin + (point.lat - center.lat) * cos;
    return { lat: y, lng: x };
}
